/**
 * Workspace Hub
 * 
 * Premium workspace management with dashboard overview,
 * quick actions, activity feed, and team integration.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faUser,
  faPlus,
  faCog,
  faTrash,
  faExchangeAlt,
  faUsers,
  faBox,
  faTag,
  faCheck,
  faTimes,
  faCrown,
  faArrowRight,
  faEdit,
  faRocket,
  faGlobe,
  faShieldAlt,
  faChartLine,
  faEllipsisV,
  faStar,
  faSearch,
  faSort,
  faThLarge,
  faList,
  faUserPlus,
  faLock,
  faEye,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { useWorkspaces } from '../hooks/useRBAC';
import { useI18n } from '../hooks/useI18n';
import { useTheme } from '../hooks/useTheme';
import { usePlanLimits } from '../hooks/usePlanLimits';
import LimitReachedModal from '../components/LimitReachedModal';
import { PlatformPlanType } from '../types/plans';
import { 
  WorkspaceType, 
  WORKSPACE_TYPE_DISPLAY_NAMES, 
  WORKSPACE_TYPE_DESCRIPTIONS,
  WORKSPACE_ROLE_DISPLAY_NAMES,
} from '../types/rbac';
import '../styles/Workspaces.css';

type ViewMode = 'grid' | 'list';
type SortMode = 'recent' | 'name' | 'items' | 'members';

const WORKSPACE_COLORS: Record<string, string> = {
  PERSONAL: '#667eea',
  ORGANIZATION: '#764ba2',
};

const Workspaces = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { theme } = useTheme();
  const { user } = useSelector((state: RootState) => state.auth);
  const { checkWorkspaceLimit, showLimitModal, limitModalType, limitCurrent, limitMax, closeLimitModal, currentPlan } = usePlanLimits((user?.planType as PlatformPlanType) || 'FREE');
  const { 
    workspaces, 
    currentWorkspace, 
    setCurrentWorkspace,
    createWorkspace, 
    updateWorkspace,
    deleteWorkspace,
    convertToOrganization,
    loading, 
    creating,
    error,
    refresh,
  } = useWorkspaces();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingWorkspaceId, setConvertingWorkspaceId] = useState<string | null>(null);
  const [convertingInProgress, setConvertingInProgress] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [createStep, setCreateStep] = useState<1 | 2 | 3>(1);
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('workspace_favorites') || '[]')); }
    catch { return new Set(); }
  });
  
  // Create form state
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState<WorkspaceType>('PERSONAL');
  const [, setNewColor] = useState('#667eea');
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const needsOnboarding = !loading && !error && workspaces.length === 0;

  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  useEffect(() => {
    localStorage.setItem('workspace_favorites', JSON.stringify([...favorites]));
  }, [favorites]);

  // Close menu on outside click
  useEffect(() => {
    const handleClick = () => setActiveMenu(null);
    if (activeMenu) document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [activeMenu]);

  // Computed stats
  const stats = useMemo(() => {
    const totalItems = workspaces.reduce((sum, w) => sum + (w._count?.items || 0), 0);
    const totalCategories = workspaces.reduce((sum, w) => sum + (w._count?.categories || 0), 0);
    const totalMembers = workspaces.reduce((sum, w) => sum + (w._count?.members || 1), 0);
    const orgCount = workspaces.filter(w => w.type === 'ORGANIZATION').length;
    return { totalItems, totalCategories, totalMembers, orgCount };
  }, [workspaces]);

  // Filtered & sorted workspaces
  const sortedWorkspaces = useMemo(() => {
    let filtered = workspaces.filter(w => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return w.name.toLowerCase().includes(q) || (w.description || '').toLowerCase().includes(q);
    });

    // Favorites first
    filtered.sort((a, b) => {
      const aFav = favorites.has(a.id) ? 1 : 0;
      const bFav = favorites.has(b.id) ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      
      switch (sortMode) {
        case 'name': return a.name.localeCompare(b.name);
        case 'items': return (b._count?.items || 0) - (a._count?.items || 0);
        case 'members': return (b._count?.members || 0) - (a._count?.members || 0);
        default: return 0; // recent - keep API order
      }
    });

    return filtered;
  }, [workspaces, searchQuery, sortMode, favorites]);

  const toggleFavorite = (id: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      setActionError('Workspace name is required');
      return;
    }
    // Check workspace limit
    if (!checkWorkspaceLimit(workspaces.length)) return;
    try {
      setActionError(null);
      await createWorkspace({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        type: newType,
      });
      setShowCreateModal(false);
      resetCreateForm();
      setActionSuccess(`${newType === 'PERSONAL' ? 'Personal workspace' : 'Organization'} created successfully!`);
    } catch (err: any) {
      setActionError(err.response?.data?.error || err.message || 'Failed to create workspace');
    }
  };

  const resetCreateForm = () => {
    setNewName('');
    setNewDescription('');
    setNewType('PERSONAL');
    setNewColor('#667eea');
    setCreateStep(1);
  };

  const handleEdit = async () => {
    if (!selectedWorkspace || !editName.trim()) return;
    try {
      setActionError(null);
      await updateWorkspace(selectedWorkspace.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      setShowEditModal(false);
      setSelectedWorkspace(null);
      setActionSuccess('Workspace updated successfully!');
    } catch (err: any) {
      setActionError(err.response?.data?.error || err.message || 'Failed to update workspace');
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkspace) return;
    try {
      setActionError(null);
      await deleteWorkspace(selectedWorkspace.id);
      setShowDeleteModal(false);
      setSelectedWorkspace(null);
      setActionSuccess('Workspace deleted successfully!');
    } catch (err: any) {
      setActionError(err.response?.data?.error || err.message || 'Failed to delete workspace');
    }
  };

  const handleConvertToOrg = async (workspaceId: string) => {
    setConvertingWorkspaceId(workspaceId);
    setShowConvertModal(true);
    setActiveMenu(null);
  };

  const confirmConvertToOrg = async () => {
    if (!convertingWorkspaceId) return;
    try {
      setConvertingInProgress(true);
      setActionError(null);
      await convertToOrganization(convertingWorkspaceId);
      setShowConvertModal(false);
      setConvertingWorkspaceId(null);
      setActionSuccess('Workspace converted to organization! You can now invite members.');
    } catch (err: any) {
      setActionError(err.response?.data?.error || err.message || 'Failed to convert workspace');
      setShowConvertModal(false);
    } finally {
      setConvertingInProgress(false);
    }
  };

  const handleSwitchWorkspace = async (workspaceId: string) => {
    try {
      await setCurrentWorkspace(workspaceId);
      setActionSuccess('Switched workspace!');
    } catch (err: any) {
      setActionError(err.message || 'Failed to switch workspace');
    }
  };

  const openEditModal = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setEditName(workspace.name);
    setEditDescription(workspace.description || '');
    setShowEditModal(true);
    setActiveMenu(null);
  };

  const openDeleteModal = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setShowDeleteModal(true);
    setActiveMenu(null);
  };

  if (loading) {
    return (
      <div className={`wh-page theme-${theme}`}>
        <div className="wh-loading">
          <div className="wh-loading-spinner" />
          <p>{t('workspaces.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`wh-page theme-${theme}`}>
      {/* Toast Messages */}
      {actionError && (
        <div className="wh-toast wh-toast-error">
          <FontAwesomeIcon icon={faTimes} />
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)}>&times;</button>
        </div>
      )}
      {actionSuccess && (
        <div className="wh-toast wh-toast-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{actionSuccess}</span>
        </div>
      )}

      {/* Hero Header */}
      <div className="wh-hero">
        <div className="wh-hero-content">
          <div className="wh-hero-badge">
            <FontAwesomeIcon icon={faRocket} /> {t('workspaces.hub')}
          </div>
          <h1>{t('workspaces.myWorkspaces')}</h1>
          <p>{t('workspaces.manageSubtitle')}</p>
        </div>
        <button className="wh-create-btn" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{t('workspaces.new')}</span>
        </button>
      </div>

      {/* Error state — show retry instead of onboarding when fetch failed */}
      {!loading && error && workspaces.length === 0 && (
        <div className="wh-stats-row" style={{ justifyContent: 'center', padding: '40px 0' }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
            <h3 style={{ marginBottom: 8 }}>{t('workspaces.fetchError') || 'Could not load workspaces'}</h3>
            <p style={{ color: '#94a3b8', marginBottom: 16 }}>
              {t('workspaces.fetchErrorDesc') || 'This may be a temporary issue. Your workspaces are safe — please try again.'}
            </p>
            <button className="wh-create-btn" onClick={() => refresh()} style={{ margin: '0 auto' }}>
              <span>{t('workspaces.retry') || 'Retry'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      {!needsOnboarding && (
        <div className="wh-stats-row">
          <div className="wh-stat-card">
            <div className="wh-stat-icon" style={{ background: 'rgba(102,126,234,0.15)', color: '#667eea' }}>
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div className="wh-stat-info">
              <span className="wh-stat-value">{workspaces.length}</span>
              <span className="wh-stat-label">{t('workspaces.title')}</span>
            </div>
          </div>
          <div className="wh-stat-card">
            <div className="wh-stat-icon" style={{ background: 'rgba(118,75,162,0.15)', color: '#764ba2' }}>
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <div className="wh-stat-info">
              <span className="wh-stat-value">{stats.orgCount}</span>
              <span className="wh-stat-label">{t('workspaces.organizations')}</span>
            </div>
          </div>
          <div className="wh-stat-card">
            <div className="wh-stat-icon" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
              <FontAwesomeIcon icon={faBox} />
            </div>
            <div className="wh-stat-info">
              <span className="wh-stat-value">{stats.totalItems}</span>
              <span className="wh-stat-label">{t('workspaces.totalItems')}</span>
            </div>
          </div>
          <div className="wh-stat-card">
            <div className="wh-stat-icon" style={{ background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="wh-stat-info">
              <span className="wh-stat-value">{stats.totalMembers}</span>
              <span className="wh-stat-label">{t('workspaces.members')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding */}
      {needsOnboarding && (
        <div className="wh-onboarding">
          <div className="wh-onboarding-glow" />
          <div className="wh-onboarding-icon">
            <FontAwesomeIcon icon={faRocket} />
          </div>
          <h2>{t('workspaces.welcome')}</h2>
          <p>{t('workspaces.welcomeSubtitle')}</p>
          
          <div className="wh-onboard-types">
            <div 
              className={`wh-onboard-type ${newType === 'PERSONAL' ? 'selected' : ''}`}
              onClick={() => setNewType('PERSONAL')}
            >
              <div className="wh-onboard-type-icon"><FontAwesomeIcon icon={faUser} /></div>
              <h3>{t('workspaces.personal')}</h3>
              <p>{t('workspaces.personalDesc')}</p>
              <div className="wh-onboard-type-features">
                <span><FontAwesomeIcon icon={faLock} /> {t('workspaces.private')}</span>
                <span><FontAwesomeIcon icon={faBox} /> {t('workspaces.unlimitedItems')}</span>
              </div>
            </div>
            <div 
              className={`wh-onboard-type ${newType === 'ORGANIZATION' ? 'selected' : ''}`}
              onClick={() => setNewType('ORGANIZATION')}
            >
              <div className="wh-onboard-type-icon"><FontAwesomeIcon icon={faUsers} /></div>
              <h3>{t('workspaces.organization')}</h3>
              <p>{t('workspaces.orgDesc')}</p>
              <div className="wh-onboard-type-features">
                <span><FontAwesomeIcon icon={faUserPlus} /> {t('workspaces.inviteMembers')}</span>
                <span><FontAwesomeIcon icon={faShieldAlt} /> {t('workspaces.roleBasedAccess')}</span>
              </div>
            </div>
          </div>
          
          <div className="wh-onboard-form">
            <input
              type="text"
              placeholder={t('workspaces.enterName')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newName.trim() && handleCreate()}
            />
            <button 
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
            >
              {creating ? t('workspaces.creating') : t('workspaces.getStarted')}
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {!needsOnboarding && (
        <div className="wh-toolbar">
          <div className="wh-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder={t('workspaces.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="wh-search-clear" onClick={() => setSearchQuery('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <div className="wh-toolbar-actions">
            <div className="wh-sort-select">
              <FontAwesomeIcon icon={faSort} />
              <select value={sortMode} onChange={(e) => setSortMode(e.target.value as SortMode)}>
                <option value="recent">{t('workspaces.sortByRecent')}</option>
                <option value="name">{t('workspaces.sortByName')}</option>
                <option value="items">{t('workspaces.sortByItems')}</option>
                <option value="members">{t('workspaces.sortByMembers')}</option>
              </select>
            </div>
            <div className="wh-view-toggle">
              <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')} title="Grid">
                <FontAwesomeIcon icon={faThLarge} />
              </button>
              <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')} title="List">
                <FontAwesomeIcon icon={faList} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Workspace Banner */}
      {currentWorkspace && !needsOnboarding && (
        <div className="wh-current-banner">
          <div className="wh-current-icon">
            <FontAwesomeIcon icon={currentWorkspace.type === 'PERSONAL' ? faUser : faBuilding} />
          </div>
          <div className="wh-current-info">
            <span className="wh-current-label">{t('workspaces.activeWorkspace')}</span>
            <span className="wh-current-name">{currentWorkspace.name}</span>
          </div>
          <div className="wh-current-actions">
            <button onClick={() => navigate(currentWorkspace.type === 'ORGANIZATION' ? `/organizations/${currentWorkspace.id}` : `/workspaces/${currentWorkspace.id}/settings`)}>
              <FontAwesomeIcon icon={faCog} /> Settings
            </button>
            {currentWorkspace.type === 'ORGANIZATION' && (
              <button onClick={() => navigate('/users')}>
                <FontAwesomeIcon icon={faUsers} /> {t('workspaces.members')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Workspaces Grid/List */}
      {!needsOnboarding && (
        <div className={`wh-workspaces ${viewMode === 'list' ? 'wh-list-view' : 'wh-grid-view'}`}>
          {sortedWorkspaces.map((workspace) => {
            const isActive = currentWorkspace?.id === workspace.id;
            const isFav = favorites.has(workspace.id);
            const typeColor = WORKSPACE_COLORS[workspace.type] || '#667eea';
            
            return (
              <div 
                key={workspace.id} 
                className={`wh-card ${isActive ? 'wh-card-active' : ''} ${isFav ? 'wh-card-fav' : ''}`}
              >
                {/* Card Top Accent */}
                <div className="wh-card-accent" style={{ background: `linear-gradient(135deg, ${typeColor}, ${typeColor}88)` }} />
                
                <div className="wh-card-header">
                  <div className="wh-card-icon" style={{ background: `${typeColor}20`, color: typeColor }}>
                    <FontAwesomeIcon icon={workspace.type === 'PERSONAL' ? faUser : faBuilding} />
                  </div>
                  <div className="wh-card-title">
                    <h3>{workspace.name}</h3>
                    <div className="wh-card-badges">
                      <span className="wh-type-badge" style={{ color: typeColor, background: `${typeColor}15` }}>
                        {WORKSPACE_TYPE_DISPLAY_NAMES[workspace.type]}
                      </span>
                      {isActive && (
                        <span className="wh-active-badge">
                          <FontAwesomeIcon icon={faCheck} /> {t('workspaces.active')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="wh-card-menu-wrapper">
                    <button 
                      className="wh-fav-btn"
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(workspace.id); }}
                      title={isFav ? 'Unstar' : 'Star'}
                    >
                      <FontAwesomeIcon icon={faStar} className={isFav ? 'starred' : ''} />
                    </button>
                    <button 
                      className="wh-menu-btn"
                      onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === workspace.id ? null : workspace.id); }}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>
                    {activeMenu === workspace.id && (
                      <div className="wh-dropdown" onClick={(e) => e.stopPropagation()}>
                        {!isActive && (
                          <button onClick={() => { handleSwitchWorkspace(workspace.id); setActiveMenu(null); }}>
                            <FontAwesomeIcon icon={faExchangeAlt} /> {t('workspaces.switchThis')}
                          </button>
                        )}
                        {workspace.type === 'ORGANIZATION' && (
                          <button onClick={() => { navigate(`/organizations/${workspace.id}`); setActiveMenu(null); }}>
                            <FontAwesomeIcon icon={faEye} /> {t('workspaces.viewOrg')}
                          </button>
                        )}
                        {workspace.role === 'OWNER' && (
                          <>
                            <button onClick={() => openEditModal(workspace)}>
                              <FontAwesomeIcon icon={faEdit} /> {t('workspaces.edit')}
                            </button>
                            <button onClick={() => { navigate(workspace.type === 'ORGANIZATION' ? `/organizations/${workspace.id}/settings` : `/workspaces/${workspace.id}/settings`); setActiveMenu(null); }}>
                              <FontAwesomeIcon icon={faCog} /> {t('workspaces.settings')}
                            </button>
                            {workspace.type === 'PERSONAL' && (
                              <button onClick={() => { handleConvertToOrg(workspace.id); setActiveMenu(null); }}>
                                <FontAwesomeIcon icon={faUsers} /> {t('workspaces.convertToOrg')}
                              </button>
                            )}
                            <div className="wh-dropdown-divider" />
                            <button className="wh-dropdown-danger" onClick={() => openDeleteModal(workspace)}>
                              <FontAwesomeIcon icon={faTrash} /> {t('workspaces.delete')}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {workspace.description && (
                  <p className="wh-card-desc">{workspace.description}</p>
                )}

                {/* Stats Bar */}
                <div className="wh-card-stats">
                  <div className="wh-card-stat">
                    <FontAwesomeIcon icon={faCrown} />
                    <span>{WORKSPACE_ROLE_DISPLAY_NAMES[workspace.role]}</span>
                  </div>
                  <div className="wh-card-stat">
                    <FontAwesomeIcon icon={faBox} />
                    <span>{workspace._count?.items || 0}</span>
                  </div>
                  <div className="wh-card-stat">
                    <FontAwesomeIcon icon={faTag} />
                    <span>{workspace._count?.categories || 0}</span>
                  </div>
                  {workspace.type === 'ORGANIZATION' && (
                    <div className="wh-card-stat">
                      <FontAwesomeIcon icon={faUsers} />
                      <span>{workspace._count?.members || 1}</span>
                    </div>
                  )}
                </div>

                {/* Quick Action */}
                <div className="wh-card-footer">
                  {!isActive ? (
                    <button className="wh-switch-btn" onClick={() => handleSwitchWorkspace(workspace.id)}>
                      <FontAwesomeIcon icon={faExchangeAlt} /> {t('workspaces.switchToWorkspace')}
                    </button>
                  ) : (
                    <button className="wh-open-btn" onClick={() => navigate('/')}>
                      <FontAwesomeIcon icon={faArrowRight} /> {t('workspaces.openDashboard')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add New Card */}
          <div className="wh-card wh-card-add" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
            <div className="wh-add-icon">
              <FontAwesomeIcon icon={faPlus} />
            </div>
            <h3>{t('workspaces.createNew')}</h3>
            <p>{t('workspaces.personalOrOrg')}</p>
          </div>
        </div>
      )}

      {/* Empty search */}
      {!needsOnboarding && sortedWorkspaces.length === 0 && searchQuery && (
        <div className="wh-empty-search">
          <FontAwesomeIcon icon={faSearch} />
          <h3>{t('workspaces.noFound')}</h3>
          <p>No workspaces match "{searchQuery}"</p>
          <button onClick={() => setSearchQuery('')}>{t('workspaces.clearSearch')}</button>
        </div>
      )}

      {/* Create Modal - Multi-Step */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="wh-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h2>{t('workspaces.createWorkspace')}</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            {/* Step Indicator */}
            <div className="wh-modal-steps">
              <div className={`wh-step ${createStep >= 1 ? 'active' : ''} ${createStep > 1 ? 'done' : ''}`}>
                <span className="wh-step-num">1</span>
                <span>{t('workspaces.typeStep')}</span>
              </div>
              <div className="wh-step-line" />
              <div className={`wh-step ${createStep >= 2 ? 'active' : ''} ${createStep > 2 ? 'done' : ''}`}>
                <span className="wh-step-num">2</span>
                <span>{t('workspaces.detailsStep')}</span>
              </div>
              <div className="wh-step-line" />
              <div className={`wh-step ${createStep >= 3 ? 'active' : ''}`}>
                <span className="wh-step-num">3</span>
                <span>{t('workspaces.reviewStep')}</span>
              </div>
            </div>

            <div className="wh-modal-body">
              {/* Step 1: Type */}
              {createStep === 1 && (
                <div className="wh-step-content">
                  <p className="wh-step-desc">{t('workspaces.chooseType')}</p>
                  <div className="wh-create-types">
                    <div 
                      className={`wh-create-type ${newType === 'PERSONAL' ? 'selected' : ''}`}
                      onClick={() => setNewType('PERSONAL')}
                    >
                      <div className="wh-create-type-icon"><FontAwesomeIcon icon={faUser} /></div>
                      <h3>{t('workspaces.personalWorkspace')}</h3>
                      <p>{WORKSPACE_TYPE_DESCRIPTIONS['PERSONAL']}</p>
                      <ul className="wh-create-type-features">
                        <li><FontAwesomeIcon icon={faLock} /> {t('workspaces.privateSecure')}</li>
                        <li><FontAwesomeIcon icon={faBox} /> {t('workspaces.fullItemMgmt')}</li>
                        <li><FontAwesomeIcon icon={faChartLine} /> {t('workspaces.personalAnalytics')}</li>
                      </ul>
                    </div>
                    <div 
                      className={`wh-create-type ${newType === 'ORGANIZATION' ? 'selected' : ''}`}
                      onClick={() => setNewType('ORGANIZATION')}
                    >
                      <div className="wh-create-type-icon"><FontAwesomeIcon icon={faBuilding} /></div>
                      <h3>{t('workspaces.organization')}</h3>
                      <p>{WORKSPACE_TYPE_DESCRIPTIONS['ORGANIZATION']}</p>
                      <ul className="wh-create-type-features">
                        <li><FontAwesomeIcon icon={faUsers} /> {t('workspaces.teamCollaboration')}</li>
                        <li><FontAwesomeIcon icon={faShieldAlt} /> {t('workspaces.roleBasedAccess')}</li>
                        <li><FontAwesomeIcon icon={faChartLine} /> {t('workspaces.orgDashboard')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Details */}
              {createStep === 2 && (
                <div className="wh-step-content">
                  <p className="wh-step-desc">{newType === 'PERSONAL' ? t('workspaces.configurePersonal') : t('workspaces.configureOrg')}</p>
                  <div className="form-group">
                    <label>{t('workspaces.nameRequired')}</label>
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={newType === 'PERSONAL' ? 'My Collection' : 'Acme Corp'}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('workspaces.descLabel')}</label>
                    <textarea
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      placeholder={t('workspaces.whatFor')}
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Review */}
              {createStep === 3 && (
                <div className="wh-step-content">
                  <p className="wh-step-desc">{t('workspaces.reviewAndCreate')}</p>
                  <div className="wh-review-card">
                    <div className="wh-review-icon" style={{ background: `${WORKSPACE_COLORS[newType]}20`, color: WORKSPACE_COLORS[newType] }}>
                      <FontAwesomeIcon icon={newType === 'PERSONAL' ? faUser : faBuilding} />
                    </div>
                    <div className="wh-review-details">
                      <h3>{newName || t('workspaces.untitled')}</h3>
                      <span className="wh-review-type">{WORKSPACE_TYPE_DISPLAY_NAMES[newType]}</span>
                      {newDescription && <p>{newDescription}</p>}
                    </div>
                  </div>
                  <div className="wh-review-features">
                    <h4>{t('workspaces.whatYouGet')}</h4>
                    <ul>
                      <li><FontAwesomeIcon icon={faCheck} /> {t('workspaces.itemMgmtCategories')}</li>
                      <li><FontAwesomeIcon icon={faCheck} /> {t('workspaces.aiCategorization')}</li>
                      <li><FontAwesomeIcon icon={faCheck} /> {t('workspaces.analyticsDashboard')}</li>
                      {newType === 'ORGANIZATION' && (
                        <>
                          <li><FontAwesomeIcon icon={faCheck} /> {t('workspaces.teamInvitations')}</li>
                          <li><FontAwesomeIcon icon={faCheck} /> {t('workspaces.rbac')}</li>
                          <li><FontAwesomeIcon icon={faCheck} /> {t('workspaces.auditTrail')}</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="wh-modal-footer">
              {createStep > 1 && (
                <button className="btn-secondary" onClick={() => setCreateStep((createStep - 1) as 1 | 2 | 3)}>
                  {t('workspaces.back')}
                </button>
              )}
              <div style={{ flex: 1 }} />
              {createStep < 3 ? (
                <button 
                  className="btn-primary" 
                  onClick={() => setCreateStep((createStep + 1) as 1 | 2 | 3)}
                  disabled={createStep === 2 && !newName.trim()}
                >
                  {t('workspaces.continue')} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              ) : (
                <button 
                  className="btn-primary" 
                  onClick={handleCreate}
                  disabled={creating || !newName.trim()}
                >
                  {creating ? t('workspaces.creating') : t('workspaces.createWorkspace')} <FontAwesomeIcon icon={faRocket} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedWorkspace && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="wh-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h2><FontAwesomeIcon icon={faEdit} /> {t('workspaces.edit')}</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="wh-modal-body">
              <div className="form-group">
                <label>{t('workspaces.nameRequired')}</label>
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="form-group">
                <label>{t('workspaces.descLabel')}</label>
                <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={3} />
              </div>
            </div>
            <div className="wh-modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>{t('workspaces.cancel')}</button>
              <button className="btn-primary" onClick={handleEdit} disabled={!editName.trim()}>{t('workspaces.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedWorkspace && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="wh-modal wh-modal-danger" onClick={(e) => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h2><FontAwesomeIcon icon={faTrash} /> {t('workspaces.delete')}</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="wh-modal-body">
              <div className="wh-delete-warning">
                <div className="wh-delete-icon"><FontAwesomeIcon icon={faTrash} /></div>
                <h3>{t('workspaces.cannotBeUndone')}</h3>
                <p>
                  Deleting <strong>{selectedWorkspace.name}</strong> will permanently remove all 
                  {selectedWorkspace._count?.items ? ` ${selectedWorkspace._count.items} items,` : ''} 
                  {selectedWorkspace._count?.categories ? ` ${selectedWorkspace._count.categories} categories,` : ''} 
                  {' '}and all associated data.
                </p>
              </div>
            </div>
            <div className="wh-modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>{t('workspaces.cancel')}</button>
              <button className="btn-danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} /> {t('workspaces.deletePermanently')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Organization Modal */}
      {showConvertModal && (
        <div className="modal-overlay" onClick={() => { setShowConvertModal(false); setConvertingWorkspaceId(null); }}>
          <div className="wh-modal wh-modal-convert" onClick={(e) => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h2><FontAwesomeIcon icon={faBuilding} /> Convert to Organization</h2>
              <button className="close-btn" onClick={() => { setShowConvertModal(false); setConvertingWorkspaceId(null); }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="wh-modal-body">
              <div className="wh-convert-hero">
                <div className="wh-convert-icon">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <h3>Upgrade to Organization</h3>
                <p>Convert your personal workspace to an organization to unlock team collaboration features.</p>
              </div>
              <div className="wh-convert-features">
                <div className="wh-convert-feature">
                  <FontAwesomeIcon icon={faUserPlus} />
                  <div>
                    <strong>Invite Team Members</strong>
                    <span>Add colleagues and assign roles</span>
                  </div>
                </div>
                <div className="wh-convert-feature">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <div>
                    <strong>Role-Based Access Control</strong>
                    <span>Control who can view, edit, and manage</span>
                  </div>
                </div>
                <div className="wh-convert-feature">
                  <FontAwesomeIcon icon={faUsers} />
                  <div>
                    <strong>Teams & Groups</strong>
                    <span>Organize members into focused teams</span>
                  </div>
                </div>
              </div>
              <div className="wh-convert-note">
                <FontAwesomeIcon icon={faCheck} />
                <span>All your existing data will be preserved. You'll become the Owner of the organization.</span>
              </div>
            </div>
            <div className="wh-modal-footer">
              <button className="btn-secondary" onClick={() => { setShowConvertModal(false); setConvertingWorkspaceId(null); }}>
                Cancel
              </button>
              <button className="btn-primary" onClick={confirmConvertToOrg} disabled={convertingInProgress}>
                <FontAwesomeIcon icon={faBuilding} /> {convertingInProgress ? 'Converting...' : 'Convert Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Limit Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={closeLimitModal}
        limitType={limitModalType}
        currentUsage={limitCurrent}
        maxAllowed={limitMax}
        planName={currentPlan}
      />
    </div>
  );
};

export default Workspaces;
